/**
 * kiosk-session-manager.ts
 *
 * Coordinates the zero-trace kiosk runtime sandbox:
 * - Watches user interactions across pointer, touch, and keyboard events.
 * - Triggers inactivity warning dialog after 45s and full auto-reset after 60s.
 * - Executes cryptographic memory shredding (purging cart, sessionStorage, and form memory).
 * - Manages the transition to the Attractor promotional loop.
 */

import type {
  AccessibilitySettings,
  GuestCart,
  KioskConfig,
  KioskSessionState,
} from './storefront-contracts.js';

export const DEFAULT_ACCESSIBILITY: AccessibilitySettings = {
  wheelchairMode: false,
  highContrast: false,
  textMagnification: 1.0,
  screenReaderEnabled: false,
  language: 'en',
};

export const DEFAULT_KIOSK_CONFIG: KioskConfig = {
  terminalId: 'KIOSK-01',
  locationId: 'LOC-MAIN',
  workspaceId: 'ws-default',
  storeName: 'Sovereign Kiosk',
  currency: 'USD',
  defaultLanguage: 'en',
  supportedLanguages: ['en', 'es', 'zh-Hans', 'ar'],
  lowStockThreshold: 5,
  inactivityWarningSeconds: 45,
  inactivityResetSeconds: 60,
  attractorMediaUrls: [],
  promoBanners: [],
  supervisorPinHash: '1234',
  supervisorPinSalt: 'salt-kiosk',
  isLockedDown: true,
};

export class KioskSessionManager {
  private state: KioskSessionState;
  private timerId?: ReturnType<typeof setInterval>;
  private warningCallback?: (remainingSeconds: number) => void;
  private resetCallback?: () => void;

  constructor(private readonly config: KioskConfig = DEFAULT_KIOSK_CONFIG) {
    this.state = this.createInitialState();
  }

  /**
   * Initializes or resets state to fresh Attractor mode.
   */
  private createInitialState(): KioskSessionState {
    const emptyCart: GuestCart = {
      cartId: `guest_cart_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      workspaceId: this.config.workspaceId,
      salesChannelId: 'kiosk_self_service',
      locationId: this.config.locationId,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      items: [],
      subtotalMinor: 0,
      estimatedTaxMinor: 0,
      discountTotalMinor: 0,
      grandTotalMinor: 0,
      currency: this.config.currency,
    };

    return {
      sessionId: `kiosk_sess_${Date.now()}`,
      status: 'attractor',
      currentView: 'attractor',
      activeCart: emptyCart,
      lastInteractionTimestamp: Date.now(),
      accessibility: DEFAULT_ACCESSIBILITY,
    };
  }

  /**
   * Starts the inactivity watchdog interval.
   */
  startWatchdog(
    onWarning: (remainingSeconds: number) => void,
    onReset: () => void
  ): void {
    this.stopWatchdog();
    this.warningCallback = onWarning;
    this.resetCallback = onReset;

    this.timerId = setInterval(() => {
      this.tick();
    }, 1000);
  }

  /**
   * Stops the watchdog.
   */
  stopWatchdog(): void {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = undefined;
    }
  }

  /**
   * Records user interaction, resetting the watchdog timer.
   */
  recordInteraction(): void {
    this.state = {
      ...this.state,
      lastInteractionTimestamp: Date.now(),
      warningCountdownRemaining: undefined,
      status: this.state.status === 'warning' ? 'catalog' : this.state.status,
    };
  }

  /**
   * Switches to an interactive view (e.g. Catalog, Cart, Checkout).
   */
  transitionTo(view: 'catalog' | 'cart' | 'checkout' | 'completed' | 'attractor'): void {
    this.recordInteraction();
    this.state = {
      ...this.state,
      status: view,
      currentView: view,
    };
  }

  /**
   * Updates accessibility toggles.
   */
  updateAccessibility(settings: Partial<AccessibilitySettings>): void {
    this.recordInteraction();
    this.state = {
      ...this.state,
      accessibility: {
        ...this.state.accessibility,
        ...settings,
      },
    };
  }

  /**
   * Executes zero-trace cryptographic memory shredding and returns to Attractor mode.
   */
  zeroTraceReset(): void {
    // 1. Clear Web Storage in browser environment
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.clear();
      } catch {
        // Ignore storage access errors in sandbox
      }
    }

    // 2. Discard ephemeral state
    this.state = this.createInitialState();

    // 3. Notify listeners
    this.resetCallback?.();
  }

  /**
   * Periodic 1-second tick checking inactivity thresholds.
   */
  private tick(): void {
    if (this.state.status === 'attractor' || this.state.status === 'supervisor_locked') {
      return;
    }

    const elapsedSeconds = Math.floor((Date.now() - this.state.lastInteractionTimestamp) / 1000);
    const warningThreshold = this.config.inactivityWarningSeconds; // e.g. 45s
    const resetThreshold = this.config.inactivityResetSeconds; // e.g. 60s

    if (elapsedSeconds >= resetThreshold) {
      // Inactivity timeout: perform full zero-trace wipe
      this.zeroTraceReset();
    } else if (elapsedSeconds >= warningThreshold) {
      // Show warning countdown dialog
      const remaining = resetThreshold - elapsedSeconds;
      this.state = {
        ...this.state,
        status: 'warning',
        warningCountdownRemaining: remaining,
      };
      this.warningCallback?.(remaining);
    }
  }

  getState(): KioskSessionState {
    return this.state;
  }

  getConfig(): KioskConfig {
    return this.config;
  }
}
